import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import produce from "immer";
import { first } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { useHistory } from "react-router-dom";
import { ApiResponse, fetchApi } from "../../utils/api";
import {
  CardPaymentMethod,
  Coupon,
  Invoice,
  NewPlan,
  PaymentStep,
  Plan,
  PlanType
} from "../../utils/types";
import { useToast } from "../toast";
import { useWorkspaces } from "../workspaces";

interface Props {}

export type ContextValue = {
  plans: Plan[];
  prePaid: Plan[];
  currentPlan: Plan | undefined;
  newPlan: NewPlan;
  setNewPlan: React.Dispatch<React.SetStateAction<NewPlan>>;
  invoices: Invoice[];
  paymentStep: PaymentStep | null;
  setPaymentStep: React.Dispatch<React.SetStateAction<PaymentStep | null>>;
  fetchInvoices: () => Promise<void>;
  loading: {
    billingPlans: boolean;
    invoices: boolean;
    stripe: boolean;
    subscribe: boolean;
    paymentMethods: boolean;
    changeDefaultPaymentMethod: boolean;
    deletePaymentMethod: boolean;
    validateCoupon: boolean;
    buyHotspotPrePaid: boolean;
    buySimulationPrePaid: boolean;
  };
  pricePerHotspot: number;
  pricePerSimulation: number;
  publishableKey: string;
  clientSecret: string | undefined;
  subscribePlan: (
    useCurrentPrePaidItems?: any,
    plan?: Plan | undefined,
    message?: string
  ) => Promise<void>;
  updatePaymentMethod: (
    values: PaymentMethodValues
  ) => Promise<ApiResponse<[]>>;
  paymentMethods: CardPaymentMethod[];
  defaultPaymentMethod: CardPaymentMethod | undefined;
  fetchPaymentMethods: () => Promise<void>;
  addPaymentMethod: (paymentMethodId?: string) => Promise<void>;
  changeDefaultPaymentMethod: (paymentMethodId?: string) => Promise<void>;
  deletePaymentMethod: (paymentMethodId?: string) => Promise<void>;
  updateBillingDetails: (
    values: BillingDetailsValues
  ) => Promise<ApiResponse<[]>>;
  validateCoupon: (couponCode?: string) => Promise<void>;
  getPrePaidPlanByType: (type: PlanType) => Plan | undefined;
  cancelDowngrade: () => Promise<void>;
  subscriptionPrePaid: (
    planType: PlanType,
    includeAmout?: number,
    redirect?: boolean
  ) => Promise<void>;
  pricePerItem: (amount: number, type: PlanType) => number;
  getPlanByType: (type: PlanType) => Plan | undefined;
  createPaymentIntent: () => Promise<void>;
  setClientSecret: React.Dispatch<React.SetStateAction<string | undefined>>;
  createCheckoutSession: () => Promise<
    | ApiResponse<{
        session_id: string;
      }>
    | undefined
  >;
};

export const BillingPlansContext = React.createContext<
  ContextValue | undefined
>(undefined);

export const BillingPlansProvider: React.FC<Props> = ({
  children,
  ...otherProps
}) => {
  const history = useHistory();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [prePaid, setPrePaid] = useState<Plan[]>([]);
  const { currentWorkspaceId, currentWorkspace, fetchWorkspaceById } =
    useWorkspaces();
  const [paymentStep, setPaymentStep] = useState<PaymentStep | null>(
    localStorage.getItem("paymentStep")
      ? Number(localStorage.getItem("paymentStep"))
      : PaymentStep.plans
  );
  const { addToast } = useToast();
  const [newPlan, setNewPlan] = useState<NewPlan>({
    hotspotAmount: 0,
    simulationAmount: 0,
    hotspotAmountPrice: 0,
    simulationAmountPrice: 0,
    trial: 0,
    total: "0.00",
    discount: "0.00",
    paymentMethod: null,
    cardHolderName: "",
    coupon: {
      id: ""
    }
  });
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState<{
    billingPlans: boolean;
    invoices: boolean;
    stripe: boolean;
    subscribe: boolean;
    paymentMethods: boolean;
    changeDefaultPaymentMethod: boolean;
    deletePaymentMethod: boolean;
    validateCoupon: boolean;
    buyHotspotPrePaid: boolean;
    buySimulationPrePaid: boolean;
  }>({
    billingPlans: false,
    invoices: false,
    stripe: true,
    subscribe: false,
    paymentMethods: true,
    changeDefaultPaymentMethod: false,
    deletePaymentMethod: false,
    validateCoupon: false,
    buyHotspotPrePaid: false,
    buySimulationPrePaid: false
  });
  const [publishableKey, setPublishableKey] = useState("");
  const [clientSecret, setClientSecret] = useState<string | undefined>();
  const [paymentMethods, setPaymentMethods] = useState<CardPaymentMethod[]>([]);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState<
    CardPaymentMethod | undefined
  >();

  const currentPlan = useMemo(
    () =>
      plans?.find((item) => item.uuid === currentWorkspace?.active_plan?.uuid),
    [currentWorkspace?.active_plan?.uuid, plans]
  );

  const getPlanByType = useCallback(
    (type: PlanType) => plans?.find((plan) => plan.type === type),
    [plans]
  );

  useEffect(() => {
    const storageNewPlan = localStorage.getItem("newPlan");

    if (storageNewPlan) {
      setNewPlan(JSON.parse(storageNewPlan));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("newPlan", JSON.stringify(newPlan));
  }, [newPlan]);

  useEffect(() => {
    localStorage.setItem("paymentStep", JSON.stringify(paymentStep));
  }, [paymentStep]);

  useEffect(() => {
    if (newPlan.plan) return;

    const plan = getPlanByType(PlanType.pro);

    setNewPlan(() => ({
      plan: plan,
      total: ((plan?.price || 0) / 100).toFixed(2),
      hotspotAmount: 0,
      simulationAmount: 0,
      hotspotAmountPrice: 0,
      simulationAmountPrice: 0,
      trial: 0,
      discount: "0.00",
      paymentMethod: null,
      cardHolderName: "",
      coupon: {
        name: ""
      }
    }));
  }, [getPlanByType, newPlan.plan, plans]);

  useEffect(() => {
    setNewPlan((prevState) => ({
      ...prevState,
      paymentMethod: defaultPaymentMethod
    }));
  }, [defaultPaymentMethod]);

  useEffect(() => {
    if (!currentWorkspace?.active_plan) return;

    const { allowed_hotspots } = currentWorkspace.active_plan;

    setNewPlan((prevState) => ({
      ...prevState,
      hotspotAmount:
        allowed_hotspots === 1
          ? currentWorkspace.counts.hotspot
          : allowed_hotspots
    }));
  }, [currentWorkspace?.active_plan, currentWorkspace?.counts.hotspot]);

  useEffect(() => {
    if (!currentWorkspace?.active_plan) return;

    const { allowed_simulations } = currentWorkspace.active_plan;

    setNewPlan((prevState) => ({
      ...prevState,
      simulationAmount: allowed_simulations
    }));
  }, [currentPlan?.simulations, currentWorkspace?.active_plan]);

  const getPrePaidPlanByType = useCallback(
    (type: PlanType) => prePaid?.find((plan) => plan.type === type),
    [prePaid]
  );

  const pricePerItem = useCallback(
    (amount: number, type: PlanType) => {
      const filterPlan = getPrePaidPlanByType(type);

      const filterUnitAmout = filterPlan?.price_tiers?.filter((item) => {
        if (item.up_to === null) return true;

        return amount <= item.up_to;
      });

      return (first(filterUnitAmout)?.unit_amount || 0) / 100;
    },
    [getPrePaidPlanByType]
  );

  const pricePerHotspot = useMemo(
    () => pricePerItem(newPlan.hotspotAmount, PlanType.prePaidHotspot),
    [newPlan.hotspotAmount, pricePerItem]
  );

  const pricePerSimulation = useMemo(
    () => pricePerItem(newPlan.simulationAmount, PlanType.prePaidSimulation),
    [newPlan.simulationAmount, pricePerItem]
  );

  const hotspotAmountPrice = useMemo(
    () => newPlan.hotspotAmount * pricePerHotspot,
    [newPlan.hotspotAmount, pricePerHotspot]
  );

  const simulationAmountPrice = useMemo(
    () => newPlan.simulationAmount * pricePerSimulation,
    [newPlan.simulationAmount, pricePerSimulation]
  );

  const total = useMemo(() => {
    let prePaidedAmountPrice = 0;

    if (newPlan.plan?.type === PlanType.community) return "0.00";

    prePaidedAmountPrice += hotspotAmountPrice + simulationAmountPrice;

    let price = (newPlan.plan?.price || 0) / 100 + prePaidedAmountPrice;

    if (newPlan.coupon.valid && newPlan.coupon.amount_off) {
      price -= newPlan.coupon.amount_off / 100;
    }

    if (newPlan.coupon.valid && newPlan.coupon.percent_off) {
      price -= (price * newPlan.coupon.percent_off) / 100;
    }

    if (price < 0) price = 0;

    return price.toFixed(2);
  }, [newPlan.coupon, newPlan.plan, hotspotAmountPrice, simulationAmountPrice]);

  useEffect(() => {
    setNewPlan((prevState) => ({
      ...prevState,
      total,
      hotspotAmountPrice,
      simulationAmountPrice
    }));
  }, [hotspotAmountPrice, newPlan.plan?.type, simulationAmountPrice, total]);

  const fetchBillingPlans = useCallback(async () => {
    setLoading((prevState) => ({ ...prevState, billingPlans: true }));

    try {
      const response = await fetchApi(`api/plans`);

      const apiResponse: ApiResponse<{
        plans: Plan[];
        "pre-paid": Plan[];
      }> = await response.json();

      if (!apiResponse.status) throw new Error();

      setPlans(apiResponse.data.plans);
      setPrePaid(apiResponse.data["pre-paid"]);
      setLoading((prevState) => ({ ...prevState, billingPlans: false }));
    } catch {
      setLoading((prevState) => ({ ...prevState, billingPlans: false }));
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    setLoading((prevState) => ({ ...prevState, invoices: true }));

    if (!currentWorkspaceId) return;

    try {
      const response = await fetchApi(
        `api/workspace/${currentWorkspaceId}/billing-invoices`
      );

      const apiResponse: ApiResponse<{
        invoices: Invoice[];
        next_payment_due: string;
        next_bill_amount: string;
      }> = await response.json();

      setInvoices(apiResponse.data.invoices);
      setLoading((prevState) => ({ ...prevState, invoices: false }));
    } catch {
      setLoading((prevState) => ({ ...prevState, invoices: false }));
    }
  }, [currentWorkspaceId]);

  const fetchPublishableKey = useCallback(async () => {
    if (!currentWorkspaceId) return;

    try {
      const response = await fetchApi(
        `api/workspace/${currentWorkspaceId}/billing/publishable-key`
      );

      const apiResponse: ApiResponse<{ key: string }> = await response.json();

      setPublishableKey(apiResponse.data.key);
    } catch {}
  }, [currentWorkspaceId]);

  const createPaymentIntent = useCallback(async () => {
    if (!currentWorkspaceId) return;

    try {
      const response = await fetchApi(
        `api/workspace/${currentWorkspaceId}/billing/payment-intent`
      );

      const apiResponse: ApiResponse<{ client_secret: string }> =
        await response.json();

      setClientSecret(apiResponse.data.client_secret);
    } catch {}
  }, [currentWorkspaceId]);

  const subscribePlan = useCallback(
    async (
      useCurrentPrePaidItems = false,
      plan?: Plan,
      successMessage?: string
    ) => {
      if (!currentWorkspaceId) return;

      setLoading((prevState) => ({ ...prevState, subscribe: true }));

      const prePaidHotspotId = getPrePaidPlanByType(
        PlanType.prePaidHotspot
      )?.stripe_price_id;

      const prePaidSimulationId = getPrePaidPlanByType(
        PlanType.prePaidSimulation
      )?.stripe_price_id;

      try {
        let data = {
          workspace: currentWorkspaceId,
          plan: plan ? plan.stripe_price_id : newPlan.plan?.stripe_price_id,
          paymentMethod: newPlan.paymentMethod?.id,
          coupon: newPlan.coupon.valid ? newPlan.coupon.id : null
        };

        if (prePaidHotspotId) {
          let prePaidHotspotAmount = newPlan.hotspotAmount;

          if (useCurrentPrePaidItems) {
            prePaidHotspotAmount =
              (currentWorkspace?.active_plan?.allowed_hotspots || 0) -
              (currentPlan?.hotspots || 0);
          }

          data = {
            ...data,
            [prePaidHotspotId]: prePaidHotspotAmount
          };
        }

        if (prePaidSimulationId) {
          let prePaidSimulationAmount = newPlan.simulationAmount;

          if (useCurrentPrePaidItems) {
            prePaidSimulationAmount =
              (currentWorkspace?.active_plan?.allowed_simulations || 0) -
              (currentPlan?.simulations || 0);
          }

          data = {
            ...data,
            [prePaidSimulationId]: prePaidSimulationAmount
          };
        }

        const response = await fetchApi(
          `api/workspace/${currentWorkspaceId}/billing/subscribe-plan`,
          {
            method: "POST",
            body: JSON.stringify(data)
          }
        );

        const apiResponse: ApiResponse<[]> = await response.json();

        if (apiResponse.status) {
          await fetchWorkspaceById(currentWorkspaceId);
          fetchInvoices();
          history.push("/workspace/pricing/success", {
            message: successMessage
          });

          setLoading((prevState) => ({ ...prevState, subscribe: false }));

          return;
        }

        history.push("/workspace/pricing/failed", {
          message: apiResponse.message || null
        });
      } catch {
        setLoading((prevState) => ({ ...prevState, subscribe: false }));

        history.push("/workspace/pricing/failed");
      }
    },
    [
      currentPlan?.hotspots,
      currentPlan?.simulations,
      currentWorkspace?.active_plan?.allowed_hotspots,
      currentWorkspace?.active_plan?.allowed_simulations,
      currentWorkspaceId,
      fetchInvoices,
      fetchWorkspaceById,
      getPrePaidPlanByType,
      history,
      newPlan.coupon.id,
      newPlan.coupon.valid,
      newPlan.hotspotAmount,
      newPlan.paymentMethod?.id,
      newPlan.plan?.stripe_price_id,
      newPlan.simulationAmount
    ]
  );

  const fetchPaymentMethods = useCallback(async () => {
    if (!currentWorkspaceId) return;

    setLoading((prevState) => ({ ...prevState, paymentMethods: true }));

    try {
      const response = await fetchApi(
        `api/workspace/${currentWorkspaceId}/billing/payment/list`
      );

      const apiResponse: ApiResponse<{
        defaultPaymentMethod: string;
        paymentMethods: CardPaymentMethod[];
      }> = await response.json();

      setPaymentMethods(apiResponse.data.paymentMethods);
      setDefaultPaymentMethod(
        apiResponse.data.paymentMethods.find(
          (item) => item.id === apiResponse.data.defaultPaymentMethod
        )
      );

      setLoading((prevState) => ({ ...prevState, paymentMethods: false }));
    } catch {
      setLoading((prevState) => ({ ...prevState, paymentMethods: false }));
    }
  }, [currentWorkspaceId]);

  const addPaymentMethod = useCallback(
    async (paymentMethodId?: string) => {
      if (!currentWorkspaceId || !paymentMethodId) return;

      try {
        const response = await fetchApi(
          `api/workspace/${currentWorkspaceId}/billing/payment/add`,
          {
            method: "POST",
            body: JSON.stringify({ paymentMethod: paymentMethodId })
          }
        );

        await response.json();

        fetchPaymentMethods();
      } catch {}
    },
    [currentWorkspaceId, fetchPaymentMethods]
  );

  const updatePaymentMethod = useCallback(
    async (values: PaymentMethodValues) => {
      const response = await fetchApi(
        `api/workspace/${currentWorkspaceId}/billing/payment/update`,
        {
          method: "POST",
          body: JSON.stringify({ ...values })
        }
      );

      const apiResponse: ApiResponse<[]> = await response.json();

      return apiResponse;
    },
    [currentWorkspaceId]
  );

  const changeDefaultPaymentMethod = useCallback(
    async (paymentMethodId?: string) => {
      if (!currentWorkspaceId || !paymentMethodId) return;

      setLoading((prevState) => ({
        ...prevState,
        changeDefaultPaymentMethod: true
      }));

      try {
        const response = await fetchApi(
          `api/workspace/${currentWorkspaceId}/billing/payment/default`,
          {
            method: "PUT",
            body: JSON.stringify({ paymentMethod: paymentMethodId })
          }
        );

        await response.json();

        await fetchPaymentMethods();
        setLoading((prevState) => ({
          ...prevState,
          changeDefaultPaymentMethod: false
        }));
      } catch {
        setLoading((prevState) => ({
          ...prevState,
          changeDefaultPaymentMethod: false
        }));
      }
    },
    [currentWorkspaceId, fetchPaymentMethods]
  );

  const deletePaymentMethod = useCallback(
    async (paymentMethodId?: string) => {
      if (!currentWorkspaceId || !paymentMethodId) return;

      if (paymentMethodId === defaultPaymentMethod?.id) {
        addToast({
          type: "error",
          message: "You cannot delete a default payment"
        });

        return;
      }

      setLoading((prevState) => ({
        ...prevState,
        deletePaymentMethod: true
      }));

      try {
        const response = await fetchApi(
          `api/workspace/${currentWorkspaceId}/billing/payment/delete`,
          {
            method: "POST",
            body: JSON.stringify({ paymentMethod: paymentMethodId })
          }
        );

        await response.json();

        await fetchPaymentMethods();
        setLoading((prevState) => ({
          ...prevState,
          deletePaymentMethod: false
        }));
      } catch {
        setLoading((prevState) => ({
          ...prevState,
          deletePaymentMethod: false
        }));
      }
    },
    [
      addToast,
      currentWorkspaceId,
      defaultPaymentMethod?.id,
      fetchPaymentMethods
    ]
  );

  const updateBillingDetails = useCallback(
    async (values: BillingDetailsValues) => {
      const response = await fetchApi(
        `api/workspace/${currentWorkspaceId}/billing-details`,
        {
          method: "PUT",
          body: JSON.stringify({ ...values })
        }
      );

      const apiResponse: ApiResponse<[]> = await response.json();

      return apiResponse;
    },
    [currentWorkspaceId]
  );

  const validateCoupon = useCallback(
    async (couponCode?: string) => {
      setLoading((prevState) => ({ ...prevState, validateCoupon: true }));

      try {
        const response = await fetchApi(
          `api/workspace/${currentWorkspaceId}/billing/coupon`,
          {
            method: "POST",
            body: JSON.stringify({ couponCode })
          }
        );

        const apiResponse: ApiResponse<Coupon> = await response.json();

        if (apiResponse.status) {
          setNewPlan((prevState) =>
            produce(prevState, (draft) => {
              draft.coupon = apiResponse.data;
              draft.coupon.valid = true;
            })
          );
        }

        if (!apiResponse.status) {
          setNewPlan((prevState) =>
            produce(prevState, (draft) => {
              draft.coupon.valid = false;
            })
          );
        }

        setLoading((prevState) => ({ ...prevState, validateCoupon: false }));
      } catch {
        setLoading((prevState) => ({ ...prevState, validateCoupon: false }));
      }
    },
    [currentWorkspaceId]
  );

  const cancelDowngrade = useCallback(async () => {
    if (!currentWorkspaceId) return;

    try {
      const response = await fetchApi(
        `api/workspace/${currentWorkspaceId}/billing/cancel-downgrade`,
        {
          method: "POST"
        }
      );

      await response.blob();

      await fetchWorkspaceById(currentWorkspaceId);

      history.push("/workspace/pricing/success", {
        message:
          "Downgrade cancelled successfully. Go to your workspace and check it out!"
      });
    } catch {
      addToast({
        type: "error",
        message: "could not cancel downgrade, an error occurred, try again"
      });
    }
  }, [addToast, currentWorkspaceId, fetchWorkspaceById, history]);

  const subscriptionPrePaid = useCallback(
    async (
      planType: PlanType,
      includeAmout: number = 0,
      redirect: boolean = true
    ) => {
      if (!currentWorkspaceId) return;

      const product_id = getPrePaidPlanByType(planType)?.stripe_price_id;

      if (!product_id) return;

      setLoading((prevState) => ({
        ...prevState,
        [planType === PlanType.prePaidHotspot
          ? "buyHotspotPrePaid"
          : "buySimulationPrePaid"]: true
      }));

      try {
        const response = await fetchApi(
          `api/workspace/${currentWorkspaceId}/billing/subscription/pre-paid`,
          {
            method: "POST",
            body: JSON.stringify({
              [product_id]:
                planType === PlanType.prePaidHotspot
                  ? newPlan.hotspotAmount + includeAmout
                  : newPlan.simulationAmount + includeAmout
            })
          }
        );

        const apiResponse: ApiResponse<[]> = await response.json();

        if (apiResponse.status) {
          await fetchWorkspaceById(currentWorkspaceId);

          const message = "You successfully upgraded your workspace.";

          redirect &&
            history.push("/workspace/pricing/success", {
              message
            });

          if (!redirect) {
            addToast({
              type: "success",
              message
            });
          }

          localStorage.removeItem("newPlan");
          localStorage.removeItem("paymentStep");
        }

        setLoading((prevState) => ({
          ...prevState,
          [planType === PlanType.prePaidHotspot
            ? "buyHotspotPrePaid"
            : "buySimulationPrePaid"]: false
        }));
      } catch {
        setLoading((prevState) => ({
          ...prevState,
          [planType === PlanType.prePaidHotspot
            ? "buyHotspotPrePaid"
            : "buySimulationPrePaid"]: false
        }));

        history.push("/workspace/pricing/failed");
      }
    },
    [
      addToast,
      currentWorkspaceId,
      fetchWorkspaceById,
      getPrePaidPlanByType,
      history,
      newPlan.hotspotAmount,
      newPlan.simulationAmount
    ]
  );

  const createCheckoutSession = useCallback(async () => {
    if (!currentWorkspaceId) return;

    try {
      let data = {
        plan: newPlan.plan?.stripe_price_id,
        hotspots: newPlan.hotspotAmount,
        simulations: newPlan.simulationAmount,
        success_url: `${process.env.REACT_APP_BASE_URL}/workspace/pricing/success`,
        cancel_url: `${process.env.REACT_APP_BASE_URL}/workspace/pricing/failed`
      };

      const response = await fetchApi(
        `api/workspace/${currentWorkspaceId}/billing/create-checkout-session`,
        {
          method: "POST",
          body: JSON.stringify(data)
        }
      );

      const apiResponse: ApiResponse<{ session_id: string }> =
        await response.json();

      return apiResponse;
    } catch {
      addToast({
        type: "error",
        message: "Something went wrong, please try again"
      });
    }
  }, [
    addToast,
    currentWorkspaceId,
    newPlan.hotspotAmount,
    newPlan.plan?.stripe_price_id,
    newPlan.simulationAmount
  ]);

  useEffect(() => {
    fetchBillingPlans();
  }, [fetchBillingPlans]);

  useEffect(() => {
    if (!currentWorkspaceId) return;

    fetchPublishableKey();
    fetchPaymentMethods();
    fetchInvoices();
  }, [
    currentWorkspaceId,
    fetchInvoices,
    fetchPaymentMethods,
    fetchPublishableKey
  ]);

  const stripeInstance = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey]
  );

  const value = useMemo(
    () => ({
      plans,
      prePaid,
      currentPlan,
      newPlan,
      setNewPlan,
      invoices,
      paymentStep,
      setPaymentStep,
      fetchInvoices,
      loading,
      pricePerItem,
      pricePerHotspot,
      pricePerSimulation,
      publishableKey,
      clientSecret,
      subscribePlan,
      updatePaymentMethod,
      paymentMethods,
      defaultPaymentMethod,
      addPaymentMethod,
      changeDefaultPaymentMethod,
      deletePaymentMethod,
      fetchPaymentMethods,
      updateBillingDetails,
      validateCoupon,
      getPrePaidPlanByType,
      cancelDowngrade,
      subscriptionPrePaid,
      getPlanByType,
      createPaymentIntent,
      setClientSecret,
      createCheckoutSession
    }),
    [
      plans,
      prePaid,
      currentPlan,
      newPlan,
      setNewPlan,
      invoices,
      paymentStep,
      setPaymentStep,
      fetchInvoices,
      loading,
      pricePerItem,
      pricePerHotspot,
      pricePerSimulation,
      publishableKey,
      clientSecret,
      subscribePlan,
      updatePaymentMethod,
      paymentMethods,
      defaultPaymentMethod,
      addPaymentMethod,
      changeDefaultPaymentMethod,
      deletePaymentMethod,
      fetchPaymentMethods,
      updateBillingDetails,
      validateCoupon,
      getPrePaidPlanByType,
      cancelDowngrade,
      subscriptionPrePaid,
      getPlanByType,
      createPaymentIntent,
      setClientSecret,
      createCheckoutSession
    ]
  );

  return (
    <BillingPlansContext.Provider value={value} {...otherProps}>
      <Elements stripe={stripeInstance}>{children}</Elements>
    </BillingPlansContext.Provider>
  );
};

export const useBillingPlans = (): ContextValue => {
  const context = useContext(BillingPlansContext);

  if (context === undefined) {
    throw new Error(
      "useBillingPlans must be used within an BillingPlansProvider"
    );
  }

  return context;
};

//
// Utils
//

export interface BillingDetailsValues {
  billing_name: string;
  billing_email: string;
  address: {
    line1: string;
    line2: string;
    postal_code: string;
    city: string;
    state: string;
    country: string;
  };
  tax: {
    type?: string;
    value?: string;
  };
}

export interface PaymentMethodValues {
  id: string;
  billing_name: string;
  billing_email: string;
  address: {
    city: string;
    country: string;
    line1: string;
    line2: string;
    postal_code: string;
    state: string;
  };
}
